import React from 'react';
import {
  useTable,
  useFlexLayout,
  useResizeColumns,
  useSortBy,
  usePagination,
} from 'react-table';
import { cx } from 'emotion';
import NewPagination from '../NewPagination';

const TableContainer = props => {
  const {
    className = '',
    data,
    columns,
    loading = false,
    getTdProps = () => {},
    trClassName = '',
    getTrProps = () => {},
    paginationClassName,
    noDataText = 'No rows found',
    showPagination = true,
    showPageSizeOptions = true,
    defaultPageSize = 20,
    query,
    setQuery,
    fixedHeight,
  } = props;

  /* Defining default column width for every column */
  const defaultColumn = {
    width: 100,
    maxWidth: 400,
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    // rows,
    page,
    prepareRow,
    // below new props related to 'usePagination' hook
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state,
  } = useTable(
    {
      columns,

      // disableResizing: true,
      data,
      defaultColumn,
      initialState: {
        pageIndex: query.page ? query.page - 1 : 0,
        pageSize: query.rows ? query.rows : defaultPageSize,
        sortBy: query.sort ? query.sort : [],
        // eslint-disable-next-line array-callback-return
        hiddenColumns: columns.map(column => {
          if (column.show === false) return column.accessor || column.id;
        }),
      },
    },
    useFlexLayout,
    useResizeColumns,
    useSortBy,
    usePagination,
  );

  const { pageIndex, pageSize, sortBy } = state;
  // console.log(query);

  React.useEffect(() => {
    //  `sortBy` changed
    if (query.sort) {
      // console.log(JSON.stringify(sortBy), query.sort);
      if (JSON.stringify(sortBy) !== JSON.stringify(query.sort)) {
        if (sortBy.length === 0) {
          setQuery({ sort: undefined });
        } else {
          setQuery({ sort: sortBy });
        }
      }
    }
  }, [sortBy]);

  const isSortedAsc = column => column.isSorted && !column.isSortedDesc;

  const isSortedDesc = column => column.isSorted && column.isSortedDesc;

  const onChangeInSelect = event => {
    const newRows = Number(event.target.value);
    setPageSize(newRows);
    setQuery && setQuery({ rows: newRows });
  };

  return (
    // Applying the table props
    <div className={cx(className, 'ReactTable', 'react-table')}>
      <div {...getTableProps} className="rt-table">
        <div className="rt-thead -header">
          {// Looping over the header rows
          headerGroups.map(headerGroup => (
            // Applying the header row props
            <div {...headerGroup.getHeaderGroupProps()} className="rt-tr">
              {// Looping over the headers in each row
              headerGroup.headers.map(column => {
                /* Checking if column can be resized and applying resizable props along with
                 header cell props and finally rendering the header cell
                 */
                // console.log(column);
                return (
                  <div
                    {...column.getHeaderProps()}
                    className={cx(
                      'rt-th rt-resizable-header',
                      isSortedAsc(column) && '-sort-asc',
                      isSortedDesc(column) && '-sort-desc',
                    )}
                  >
                    <div
                      className="rt-resizable-header-content"
                      {...column.getSortByToggleProps()}
                    >
                      {column.render('Header')}
                    </div>
                    {column.canResize && (
                      <div
                        {...column.getResizerProps()}
                        className="rt-resizer"
                      />
                    )}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
        <div className="rt-tbody">
          {/* Applying the table body props */}
          <div {...getTableBodyProps()} className="rt-tr-group">
            {// Loopting over the table rows
            page.length > 0 ? (
              <React.Fragment>
                {page.map(row => {
                  // Preparing row for rendering
                  prepareRow(row);
                  // console.log(row);
                  return (
                    <React.Fragment key={row.id}>
                      {/* Applying the row props and adding classname for alternate rows */}
                      <div
                        {...row.getRowProps()}
                        className={cx('rt-tr', trClassName)}
                      >
                        {// Looping over the row cells
                        row.cells.map((cell, cellIndex) => {
                          return (
                            // Applying the cell props and rendering the cell content
                            <div
                              {...getTdProps(cell)}
                              {...getTrProps(cell)}
                              {...cell.getCellProps()}
                              className={cx('rt-td', cell.column.className)}
                            >
                              {cell.render('Cell', {
                                // gives me a unique and predictable id for the cell so why not
                                uniqueId: `${row.index}${cellIndex}`,
                              })}
                            </div>
                          );
                        })}
                      </div>
                    </React.Fragment>
                  );
                })}
                {fixedHeight &&
                  pageSize > page.length &&
                  Array(pageSize - page.length)
                    .fill('')
                    .map((_obj, index) => (
                      <React.Fragment key={`empty-${index}`}>
                        <div className="rt-tr">
                          <div className="rt-td">&nbsp;</div>
                        </div>
                      </React.Fragment>
                    ))}
              </React.Fragment>
            ) : (
              <div className="rt-tr -padRow -odd">
                <div className="rt-noData">{noDataText}</div>
              </div>
            )}
          </div>
        </div>
      </div>

      {!!showPagination && (
        <NewPagination
          {...{
            page,
            canPreviousPage,
            canNextPage,
            pageOptions,
            pageCount,
            gotoPage,
            nextPage,
            previousPage,
            setPageSize,
            pageSize,
            pageIndex,
            onChangeInSelect,
            showPageSizeOptions,
            paginationClassName,
            setQuery,
          }}
        />
      )}
      {/* Loading overlay */}
      <div className={cx('-loading', loading ? '-active' : '')}>
        <div className="-loading-inner">Loading...</div>
      </div>
    </div>
  );
};

export default TableContainer;
